import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { abortController } from 'store/utils';
import { getPlacesApi, getPlacesByDistrictApi, getPlacesByProjectApi, deletePlacesAdminApi, deletePlacesCategoryAdminApi, getPlacesCategoriesApi, getPlacesCategoryApi, getPlaceApi, getPlacesDashBoardAdminApi, createPlacesCategoryAdminApi, editPlacesCategoryAdminApi, createPlaceAdminApi, editPlaceAdminApi, getPlaceDashboardAdminApi, } from './api';
const initialState = {
    selected: undefined,
    placesByDistrict: undefined,
    placesByProject: undefined,
    places: undefined,
    categories: {
        data: [],
        total: 0,
    },
    dashboard: {
        data: [],
        total: 0,
    },
    filters: {
        search: '',
        emiratesIds: [],
        districtsIds: [],
        categoriesIds: [],
    },
};
const actions = {
    setSelected: async (place, { setState }) => {
        setState((state) => {
            if (state.selected?.id !== place?.id)
                state.selected = place;
        });
    },
    getPlaces: async (params, { setState, signal }) => {
        try {
            const { data: res } = await getPlacesApi(params, signal);
            setState((state) => {
                state.places = res;
            });
            return res;
        }
        catch (error) {
            throw error;
        }
    },
    getPlacesByDistrict: async (districtId, { setState }) => {
        try {
            const { data: res } = await getPlacesByDistrictApi(districtId);
            setState((state) => {
                state.placesByDistrict = res;
            });
            return res;
        }
        catch (error) {
            throw error;
        }
    },
    getPlacesByProject: async (projectId, { setState }) => {
        try {
            const { data: res } = await getPlacesByProjectApi(projectId);
            setState((state) => {
                state.placesByProject = res;
            });
            return res;
        }
        catch (error) {
            throw error;
        }
    },
    getPlacesCategories: async (_, { setState }) => {
        try {
            const { data } = await getPlacesCategoriesApi();
            setState((state) => {
                state.categories = data;
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    clearPlacesCategories: (_, { setState }) => {
        setState((state) => {
            state.categories = {
                data: [],
                total: 0,
            };
        });
    },
    getPlacesCategory: async (id) => {
        try {
            const { data } = await getPlacesCategoryApi(id);
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    getPlace: async (id) => {
        try {
            const { data } = await getPlaceApi(id);
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    getPlacesDashboard: async (payload, { setState, signal }) => {
        try {
            const { append = false, ...params } = payload ?? {};
            const { data } = await getPlacesDashBoardAdminApi(params, signal);
            setState((state) => {
                if (append) {
                    const existingIds = new Set(state.dashboard.data.map(({ id }) => id));
                    state.dashboard.data = [
                        ...state.dashboard.data,
                        ...data.data.filter(({ id }) => !existingIds.has(id)),
                    ];
                    state.dashboard.total = data.total;
                }
                else {
                    state.dashboard = data;
                }
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    getPlaceDashboard: async (id) => {
        try {
            const { data } = await getPlaceDashboardAdminApi(id);
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    deletePlacesCategory: async (id, { setState }) => {
        try {
            await deletePlacesCategoryAdminApi(id);
            setState((state) => {
                state.categories.data = state.categories?.data.filter((category) => category.id !== id);
            });
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    deletePlaces: async (id, { setState }) => {
        try {
            await deletePlacesAdminApi(id);
            setState((state) => {
                state.dashboard.data = state?.dashboard?.data.filter((dashboardItem) => dashboardItem.id !== id);
                state.dashboard.total = Math.max(0, (state.dashboard.total ?? 0) - 1);
            });
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    setPlacesFilters: (payload, { setState }) => {
        setState((state) => {
            state.filters = payload;
        });
    },
    createPlacesCategory: async (payload, { setState }) => {
        try {
            const { data } = await createPlacesCategoryAdminApi(payload);
            setState((state) => {
                state.categories.data.push(data);
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data ?? error;
        }
    },
    editPlacesCategory: async ({ id, ...payload }, { setState }) => {
        try {
            const { data } = await editPlacesCategoryAdminApi(id, payload);
            setState((state) => {
                const index = state.categories.data.findIndex((category) => category.id === id);
                if (index !== -1) {
                    const prev = state.categories.data[index];
                    state.categories.data[index] = {
                        ...prev,
                        ...data,
                        placesCount: prev.placesCount,
                    };
                }
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    createPlace: async (payload, { setState }) => {
        try {
            const { data } = await createPlaceAdminApi(payload);
            const dashboard = await getPlacesDashBoardAdminApi();
            setState((state) => {
                state.dashboard = dashboard.data;
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data ?? error;
        }
    },
    editPlace: async ({ id, ...payload }, { setState }) => {
        try {
            const { data } = await editPlaceAdminApi(id, payload);
            const dashboard = await getPlacesDashBoardAdminApi();
            setState((state) => {
                state.dashboard = dashboard.data;
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data ?? error;
        }
    },
};
const store = { ...initialState, ...actions };
export default create()(devtools(immer((set, get) => abortController(store, set, get, 'Places')), {
    name: 'Places',
}));
